import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Stakeholder } from '@/pages/admin/StakeholderManagement';
import { useToast } from '@/hooks/use-toast';
import { useAuthQuery } from '@/hooks/useAuthQuery';
import { chunkedInQuerySimple } from '@/lib/queries/chunkedInQuery';

export const useStakeholders = () => {
  return useAuthQuery<Stakeholder[]>({
    queryKey: ['stakeholders', 'all-with-optional-projects', 'v5'],
    queryFn: async () => {
      // Try with JOIN first, fallback to separate queries if relationship not found
      let stakeholdersResult;
      let profilesMap: Record<string, string> = {};
      
      try {
        stakeholdersResult = await supabase
          .from('stakeholders')
          .select('id, name, email, phone, notes, attachments, created_by, created_at, updated_at, profiles!created_by(full_name, avatar_url)')
          .order('created_at', { ascending: false })
          .limit(500);
        
        if (stakeholdersResult.error) throw stakeholdersResult.error;
      } catch (error: any) {
        // Fallback: fetch stakeholders and profiles separately
        if (error?.message?.includes('Could not find a relationship')) {
          stakeholdersResult = await supabase
            .from('stakeholders')
            .select('id, name, email, phone, notes, attachments, created_by, created_at, updated_at')
            .order('created_at', { ascending: false })
            .limit(500);
          
          if (stakeholdersResult.error) throw stakeholdersResult.error;
          
          // Fetch profiles separately using chunked query
          const createdByIds = stakeholdersResult.data?.map(s => s.created_by).filter(Boolean) || [];
          if (createdByIds.length > 0) {
            const profilesData = await chunkedInQuerySimple<{ id: string; full_name: string; avatar_url: string | null }>(
              supabase,
              'profiles',
              'id',
              createdByIds,
              'id, full_name, avatar_url'
            );
            
            const profilesMapData: Record<string, { name: string; avatar: string | null }> = {};
            profilesData.forEach(p => {
              profilesMapData[p.id] = { name: p.full_name, avatar: p.avatar_url };
            });
            // @ts-expect-error - profilesMap type intentionally simplified for later access
            profilesMap = profilesMapData;
          }
        } else {
          throw error;
        }
      }
      
      const assignmentsResult = await supabase
        .from('project_stakeholder_assignments')
        .select('stakeholder_id, id, role, project_id, assigned_at, updated_at');
      
      if (assignmentsResult.error) throw assignmentsResult.error;
      
      // Client-side LEFT JOIN: Map assignments by stakeholder_id
      const assignmentMap = new Map(
        assignmentsResult.data?.map(a => [a.stakeholder_id, a]) || []
      );
      
      return stakeholdersResult.data.map(sh => {
        const assignment = assignmentMap.get(sh.id);
        const profileInfo = (sh.profiles as any);
        const fallbackProfile = profilesMap[sh.created_by] as any;
        return {
          id: sh.id,
          name: sh.name,
          email: sh.email,
          phone: sh.phone,
          notes: sh.notes,
          attachments: (sh.attachments || []) as unknown as Stakeholder['attachments'],
          assignment_id: assignment?.id,
          role: assignment?.role || 'Unassigned',
          project_id: assignment?.project_id, // undefined if no assignment
          created_by: sh.created_by,
          creator_name: profileInfo?.full_name || fallbackProfile?.name || null,
          creator_avatar_url: profileInfo?.avatar_url || fallbackProfile?.avatar || null,
          created_at: sh.created_at,
          updated_at: assignment?.updated_at || sh.updated_at
        } as unknown as Stakeholder;
      });
    },
    requiresAuth: true,
    requiresSession: true,
    enabled: true,
  staleTime: 5 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  });
};

export const useAddStakeholder = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (stakeholder: Omit<Stakeholder, 'id' | 'created_at' | 'updated_at'>) => {
      // Step 1: Insert into global stakeholders table first
      const { data: globalStakeholder, error: globalError } = await supabase
        .from('stakeholders')
        .insert([{
          name: stakeholder.name,
          email: stakeholder.email,
          phone: stakeholder.phone,
        }])
        .select()
        .single();
      
      if (globalError) throw globalError;
      
      // Step 2: Insert into project_stakeholders using the newly created stakeholder_id
      const { data: projectStakeholder, error: projectError } = await supabase
        .from('project_stakeholder_assignments')
        .insert([{
          project_id: stakeholder.project_id,
          stakeholder_id: globalStakeholder.id,
          role: stakeholder.role,
          assigned_by: null
        }])
        .select()
        .single();
      
      if (projectError) throw projectError;
      
      // Return combined data for consistency
      return {
        id: projectStakeholder.id,
        name: stakeholder.name,
        email: stakeholder.email,
        phone: stakeholder.phone,
        role: stakeholder.role,
        project_id: stakeholder.project_id,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stakeholders'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['project-stakeholders'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['project-stakeholders-admin'], exact: false });
      toast({
        title: 'Thành công',
        description: 'Đã thêm stakeholder mới',
      });
    },
    onError: (error) => {
      toast({
        title: 'Lỗi',
        description: 'Không thể thêm stakeholder: ' + error.message,
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateStakeholder = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Stakeholder> & { id: string }) => {
      // Step 1: Get stakeholder_id from assignment
      const { data: assignment, error: fetchError } = await supabase
        .from('project_stakeholder_assignments')
        .select('stakeholder_id')
        .eq('id', id)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Step 2: Update stakeholders table (name, email, phone)
      const { error: stakeholderError } = await supabase
        .from('stakeholders')
        .update({
          name: updates.name,
          email: updates.email,
          phone: updates.phone
        })
        .eq('id', assignment.stakeholder_id);
      
      if (stakeholderError) throw stakeholderError;
      
      // Step 3: Update assignment table (role, project_id)
      const { error: assignmentError } = await supabase
        .from('project_stakeholder_assignments')
        .update({
          role: updates.role,
          project_id: updates.project_id
        })
        .eq('id', id);
      
      if (assignmentError) throw assignmentError;
      
      return { id, ...updates };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stakeholders'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['project-stakeholders'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['project-stakeholders-admin'], exact: false });
      toast({
        title: 'Thành công',
        description: 'Đã cập nhật stakeholder',
      });
    },
    onError: (error) => {
      toast({
        title: 'Lỗi',
        description: 'Không thể cập nhật stakeholder: ' + error.message,
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteStakeholder = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      // Only delete from project_stakeholder_assignments
      // Keep stakeholder in global table for reuse
      const { error } = await supabase
        .from('project_stakeholder_assignments')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stakeholders'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['project-stakeholders'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['project-stakeholders-admin'], exact: false });
      toast({
        title: 'Thành công',
        description: 'Đã xóa stakeholder',
      });
    },
    onError: (error) => {
      toast({
        title: 'Lỗi',
        description: 'Không thể xóa stakeholder: ' + error.message,
        variant: 'destructive',
      });
    },
  });
};

// New hooks for admin management of project stakeholders
export const useProjectStakeholdersAdmin = () => {
  return useAuthQuery({
    queryKey: ['project-stakeholders-admin', 'v4'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_stakeholder_assignments')
        .select(`
          id,
          role,
          project_id,
          assigned_at,
          updated_at,
          stakeholders!inner(
            id,
            name,
            email,
            phone,
            notes,
            attachments
          ),
          projects!inner(
            project_name,
            project_group
          )
        `)
        .order('assigned_at', { ascending: false });
      
      if (error) throw error;
      
      return data.map(item => ({
        assignment_id: item.id,
        id: item.stakeholders.id,
        name: item.stakeholders.name,
        email: item.stakeholders.email,
        phone: item.stakeholders.phone,
        notes: item.stakeholders.notes,
        attachments: item.stakeholders.attachments || [],
        role: item.role,
        source: 'manual',
        project_id: item.project_id,
        project_name: item.projects?.project_name,
        project_group: item.projects?.project_group,
        created_at: item.assigned_at,
        updated_at: item.updated_at,
      }));
    },
    requiresAuth: true,
    requiresSession: true,
    enabled: true,
  staleTime: 5 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  });
};

export const useAddProjectStakeholderAdmin = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (stakeholder: {
      name: string;
      email?: string;
      phone?: string;
      role: string;
      project_id: string;
      source?: string;
    }) => {
      // Step 1: Insert into global stakeholders table
      const { data: globalStakeholder, error: globalError } = await supabase
        .from('stakeholders')
        .insert([{
          name: stakeholder.name,
          email: stakeholder.email,
          phone: stakeholder.phone,
        }])
        .select()
        .single();
      
      if (globalError) throw globalError;
      
      // Step 2: Insert into project_stakeholder_assignments
      const { data, error } = await supabase
        .from('project_stakeholder_assignments')
        .insert([{
          project_id: stakeholder.project_id,
          stakeholder_id: globalStakeholder.id,
          role: stakeholder.role,
          assigned_by: null
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stakeholders'] });
      queryClient.invalidateQueries({ queryKey: ['project-stakeholders'] });
      queryClient.invalidateQueries({ queryKey: ['project-stakeholders-admin'] });
      toast({
        title: 'Thành công',
        description: 'Đã thêm stakeholder mới',
      });
    },
    onError: (error) => {
      toast({
        title: 'Lỗi',
        description: 'Không thể thêm stakeholder: ' + error.message,
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateProjectStakeholderAdmin = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; name: string; email?: string; phone?: string; role: string; project_id?: string }) => {
      // Step 1: Get stakeholder_id from assignment
      const { data: assignment, error: fetchError } = await supabase
        .from('project_stakeholder_assignments')
        .select('stakeholder_id')
        .eq('id', id)
        .single();
      
      if (fetchError) {
        console.error('[useUpdateProjectStakeholderAdmin] Fetch error:', fetchError);
        throw fetchError;
      }
      
      // Step 2: Update stakeholders table (name, email, phone)
      const { error: stakeholderError } = await supabase
        .from('stakeholders')
        .update({
          name: updates.name,
          email: updates.email,
          phone: updates.phone
        })
        .eq('id', assignment.stakeholder_id);
      
      if (stakeholderError) {
        console.error('[useUpdateProjectStakeholderAdmin] Stakeholder update error:', stakeholderError);
        throw stakeholderError;
      }
      
      // Step 3: Update assignment table (role, project_id)
      const { error: assignmentError } = await supabase
        .from('project_stakeholder_assignments')
        .update({
          role: updates.role,
          project_id: updates.project_id
        })
        .eq('id', id);
      
      if (assignmentError) {
        console.error('[useUpdateProjectStakeholderAdmin] Assignment update error:', assignmentError);
        throw assignmentError;
      }
      
      if (import.meta.env.DEV) {
        console.debug('[useUpdateProjectStakeholderAdmin] Updated stakeholder:', { id, updates });
      }
      return { id, ...updates };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stakeholders'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['project-stakeholders'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['project-stakeholders-admin'], exact: false });
      toast({
        title: 'Thành công',
        description: 'Đã cập nhật stakeholder',
      });
    },
    onError: (error) => {
      toast({
        title: 'Lỗi',
        description: 'Không thể cập nhật stakeholder: ' + error.message,
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteProjectStakeholder = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      // Only delete from project_stakeholder_assignments
      // Keep stakeholder in global table for reuse
      const { error } = await supabase
        .from('project_stakeholder_assignments')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stakeholders'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['project-stakeholders'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['project-stakeholders-admin'], exact: false });
      toast({
        title: 'Thành công',
        description: 'Đã xóa stakeholder',
      });
    },
    onError: (error) => {
      toast({
        title: 'Lỗi',
        description: 'Không thể xóa stakeholder: ' + error.message,
        variant: 'destructive',
      });
    },
  });
};